import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Download, Trash2, Upload, X } from 'lucide-react';
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile } from '@ffmpeg/util';
import Navbar from './Navbar';
import { sanitizeFileName } from './utils/sanitize';


import { 
    validateAudioFile, 
    RateLimiter, 
    logError, 
    initializeErrorHandling 
} from './utils/security';

// Replace or add this after your imports
const validateVideoFile = async (file) => {
    const validVideoFormats = [
    'video/mp4',          // .mp4 files
    'video/webm',         // .webm files
    'video/x-matroska',   // .mkv files
    'video/quicktime',    // .mov files
    'video/x-msvideo',    // .avi files
    'video/x-ms-wmv',     // .wmv files
    'video/mpeg',         // .mpeg, .mpg files
    'video/3gpp',         // .3gp files
    'video/x-flv',        // .flv files
    'video/ogg'           // .ogv files
];
    
    if (!file) {
        throw new Error('No file provided');
    }

    if (!validVideoFormats.includes(file.type)) {
        throw new Error('Invalid video format');
    }

    // Max file size (500MB)
    const maxSize = 500 * 1024 * 1024;
    if (file.size > maxSize) {
        throw new Error('File size exceeds 500MB limit');
    }

    return true;
};

const rateLimiter = new RateLimiter();


const ffmpeg = new FFmpeg();

const PopupMessage = ({ messages, onClose }) => {
    return (
        <div className="popup-container">
            <button className="close-btn" onClick={onClose}>
                <X size={24} />
            </button>
            <div className="popup-message">
                {messages.map((message, index) => (
                    <p key={index} className="message-text">{message}</p>
                ))}
            </div>
            <style jsx>{`
                .popup-container {
                    position: fixed;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    z-index: 1000;
                }
                .popup-message {
                    background-color: rgba(0, 0, 0, 0.7);
                    color: white;
                    padding: 10px 20px;
                    border-radius: 5px;
                    max-width: 300px;
                    text-align: center;
                    font-size: 14px;
                    max-height: 80vh;
                    overflow-y: auto;
                     word-break: break-word;    /* Add this */
    white-space: normal; 
                }
                .popup-message::-webkit-scrollbar {
                    width: 1.67vh;
                }
                .popup-message::-webkit-scrollbar-track {
                    background-color: rgba(217, 217, 217, 0.3);
                    border-radius: 1.11vh;
                }
                .popup-message::-webkit-scrollbar-thumb {
                    background-color: rgba(255, 255, 255, 0.5);
                    border-radius: 0.74vh;
                    border: 0.37vh solid transparent;
                    background-clip: padding-box;
                }
                .popup-message::-webkit-scrollbar-thumb:hover {
                    background-color: rgba(255, 255, 255, 0.7);
                }
                .close-btn {
                    position: absolute;
                    top: -30px;
                    right: -30px;
                    background: none;
                    border: none;
                    color: white;
                    cursor: pointer;
                    padding: 5px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background-color: rgba(0, 0, 0, 0.7);
                    border-radius: 50%;
                    width: 30px;
                    height: 30px;
                }
                
            `}</style>
        </div>
    );
};
const FileItem = ({ file, onDownload }) => {
    const [shouldScroll, setShouldScroll] = useState(false);
    const contentRef = useRef(null);
    const containerRef = useRef(null);

    useEffect(() => {
        const checkIfShouldScroll = () => {
            if (contentRef.current && containerRef.current) {
                const contentWidth = contentRef.current.offsetWidth;
                const containerWidth = containerRef.current.offsetWidth;
                setShouldScroll(contentWidth > containerWidth - 40);
            }
        };

        checkIfShouldScroll();
        window.addEventListener('resize', checkIfShouldScroll);
        return () => window.removeEventListener('resize', checkIfShouldScroll);
    }, [file.name]);

    return (
        <div className="file-item">
            <button className="download-btn" onClick={() => onDownload(file)}>
                <Download size={20} />
            </button>
            <div className="filename-container" ref={containerRef}>
                {!shouldScroll ? (
                    // Static view for short names
                    <div className="filename-content" ref={contentRef}>
                        <span className="file-name">{file.name}</span>
                    </div>
                ) : (
                    // Scrolling view for long names
                    <>
                        <div ref={contentRef} style={{ position: 'absolute', visibility: 'hidden', whiteSpace: 'nowrap' }}>
                            <span className="file-name">{file.name}</span>
                        </div>
                        <div className="fade-outer fade-left"></div>
                        <div className="fade-inner fade-left"></div>
                        <div className="filename-scroll">
                            <div className="filename-content">
                                <span className="file-name">{file.name}</span>
                                <span className="file-name">{file.name}</span>
                            </div>
                            <div className="filename-content">
                                <span className="file-name">{file.name}</span>
                                <span className="file-name">{file.name}</span>
                            </div>
                        </div>
                        <div className="fade-inner fade-right"></div>
                        <div className="fade-outer fade-right"></div>
                    </>
                )}
            </div>
        </div>
    );
};

const VideotoAudio = () => {
    const [ffmpegLoaded, setFfmpegLoaded] = useState(false);
    const [showTwoSquares, setShowTwoSquares] = useState(true);
    const [files, setFiles] = useState({});
    const [loadingFormats, setLoadingFormats] = useState(new Set()); // Changed to Set for format-specific loading
    const [popupMessages, setPopupMessages] = useState([]);
    const fileListRefs = useRef({});
    const fileInputRefs = useRef({});

    const audioFormats = ['mp3', 'wav', 'ogg', 'aac', 'flac'];

    const updateSquareCount = useCallback(() => {
        const squareSize = window.innerHeight * 0.3333;
        const containerWidth = window.innerWidth;
        setShowTwoSquares(containerWidth >= squareSize * 2 + window.innerWidth * 0.1);
    }, []);

   useEffect(() => {
    const loadFFmpeg = async () => {
        const ffmpegTimeout = setTimeout(() => {
            setPopupMessages(['FFmpeg loading timeout. Please refresh the page.']);
        }, 30000); // 30 second timeout
        try {
            await ffmpeg.load();
            clearTimeout(ffmpegTimeout); // Add this line
            setFfmpegLoaded(true);
        } catch (error) {
            console.error('Failed to load FFmpeg:', error);
            clearTimeout(ffmpegTimeout); // Add this line
            setPopupMessages(['Failed to load Video conversion library']);
        }
    };
    initializeErrorHandling();
    loadFFmpeg();
}, []);

    useEffect(() => {
        updateSquareCount();
        window.addEventListener('resize', updateSquareCount);
        return () => window.removeEventListener('resize', updateSquareCount);
    }, [updateSquareCount]);

    useEffect(() => {
        const checkScrollbars = () => {
            audioFormats.forEach(format => {
                const fileList = fileListRefs.current[format];
                if (fileList) {
                    const hasScrollbar = fileList.scrollHeight > fileList.clientHeight;
                    fileList.style.setProperty('--padding-right', hasScrollbar ? '1.11vh' : '0');
                }
            });
        };

        checkScrollbars();
        window.addEventListener('resize', checkScrollbars);
        return () => window.removeEventListener('resize', checkScrollbars);
    }, [files, audioFormats]);

const convertVideoToAudio = async (file, targetFormat) => {
    if (!ffmpegLoaded) {
        throw new Error('Conversion library not loaded');
    }

    const inputFileName = 'input_video.' + file.name.split('.').pop();
    const outputFileName = `output.${targetFormat}`;

    try {
        await ffmpeg.writeFile(inputFileName, await fetchFile(file));
        
        // FFmpeg command to extract audio
        await ffmpeg.exec([
            '-i', inputFileName,
            '-vn', // Skip video
            ...(targetFormat === 'mp3' ? ['-b:a', '320k'] : []),
            ...(targetFormat === 'ogg' ? ['-q:a', '10'] : []),
            ...(targetFormat === 'aac' ? ['-b:a', '320k', '-strict', '-2'] : []),
            ...(targetFormat === 'flac' ? ['-compression_level', '8'] : []),
            outputFileName
        ]);

        const data = await ffmpeg.readFile(outputFileName);
        const convertedFile = new File(
            [data.buffer],
            `${file.name.split('.')[0]}.${targetFormat}`,
            { type: `audio/${targetFormat}` }
        );

        // Cleanup
        await Promise.all([
            ffmpeg.deleteFile(inputFileName),
            ffmpeg.deleteFile(outputFileName)
        ]);

        return convertedFile;
    } catch (error) {
        console.error('Conversion error:', error);
        throw error;
    }
};

const handleFileUpload = async (event, targetFormat) => {
    if (!rateLimiter.canMakeRequest()) {
        setPopupMessages(['Too many requests. Please wait before trying again']);
        return;
    }

    if (!ffmpegLoaded) {
        setPopupMessages(['Video converter is still loading...']);
        return;
    }

    try {
        const uploadedFiles = event.dataTransfer?.files || event.target?.files;
        if (!uploadedFiles?.length) {
            throw new Error('No files selected');
        }

        setLoadingFormats(prev => new Set([...prev, targetFormat]));

        for (const file of uploadedFiles) {
            try {
                await validateVideoFile(file);  // Changed from validateAudioFile
                const sanitizedName = sanitizeFileName(file.name);
                const fileWithSanitizedName = new File([file], sanitizedName, { type: file.type });
                
                const convertedFile = await convertVideoToAudio(fileWithSanitizedName, targetFormat);  // Changed from convertAudio
                setFiles(prev => ({
                    ...prev,
                    [targetFormat]: [...(prev[targetFormat] || []), convertedFile]
                }));
            } catch (error) {
                console.error('File processing error:', error);
                setPopupMessages(prev => [...prev, `Failed to process "${file.name}": ${error.message}`]);
            }
        }
    } catch (error) {
        console.error('Upload error:', error);
        setPopupMessages(['An error occurred while processing the video files']);
    } finally {
        setLoadingFormats(prev => {
            const newSet = new Set(prev);
            newSet.delete(targetFormat);
            return newSet;
        });
    }
};

const downloadFile = (file) => {
    // GTM tracking for download
    window.dataLayer?.push({
        'event': 'file_download',
        'file_format': file.name.split('.').pop()
    });

    try {
        // Validate file before download
        if (!file || !(file instanceof File)) {
            throw new Error('Invalid file object');
        }

        // Validate file type
        const fileExtension = file.name.split('.').pop().toLowerCase();
        if (!audioFormats.includes(fileExtension)) {
            throw new Error('Invalid file type');
        }

        const sanitizedName = sanitizeFileName(file.name);
        const blob = new Blob([file], { type: file.type });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = sanitizedName;
        a.rel = 'noopener noreferrer';
        
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        
        setTimeout(() => {
            URL.revokeObjectURL(url);
        }, 100);
    } catch (error) {
        logError(error, {
            fileName: file?.name,
            operation: 'download'
        });
        setPopupMessages(['Failed to download file']);
    }
};
    const downloadAllFiles = (formatFiles) => {
        formatFiles.forEach(file => {
            downloadFile(file);
        });
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e, format) => {
        e.preventDefault();
        e.stopPropagation();
        handleFileUpload(e, format);
    };

    const clearAllFiles = (format) => {
        setFiles(prevFiles => ({
            ...prevFiles,
            [format]: []
        }));
    };

    const triggerFileInput = (format) => {
        if (fileInputRefs.current[format]) {
            fileInputRefs.current[format].value = '';
            fileInputRefs.current[format].click();
        }
    };

    const renderSquare = (format) => (
        <div
            className="square"
            onDrop={(e) => handleDrop(e, format)}
            onDragOver={handleDragOver}
            onClick={() => triggerFileInput(format)}
        >
           <div className="format format-input">Video to .{format.toLowerCase()}</div> 
            
<input
    type="file"
    multiple
    accept="video/*,.mp4,.webm,.mov,.avi,.mkv,.wmv,.mpeg,.mpg,.3gp,.flv,.ogv"
    onChange={(e) => handleFileUpload(e, format)}
    style={{ display: 'none' }}
    ref={el => fileInputRefs.current[format] = el}
/>
        </div>
    );

    const renderOutputSquare = (format) => (
        <div className={`square output ${(files[format]?.length || loadingFormats.has(format)) ? 'has-files' : ''}`}>
             {(!files[format]?.length && !loadingFormats.has(format)) ? (
            <div className="format">Output</div>
        ) : (
            <div className="file-list-container">
                <div
                    className="file-list"
                    ref={el => fileListRefs.current[format] = el}
                    onWheel={(e) => e.stopPropagation()}
                >
                    {loadingFormats.has(format) && <div className="loading">Converting video...</div>}
                    {files[format]?.map((file, idx) => (
                        <FileItem key={idx} file={file} onDownload={downloadFile} />
                    ))}
                </div>
                {(files[format]?.length > 0 || loadingFormats.has(format)) && (
                    <div className="button-container">
                        {!showTwoSquares && (
                            <button
                                className="action-btn upload-btn"
                                onClick={() => triggerFileInput(format)}
                            >
                                {format.toLowerCase()}
                            </button>
                        )}
                        {files[format]?.length > 0 && (
                            <>
                                <button
                                    className="action-btn download-all-btn"
                                    onClick={() => downloadAllFiles(files[format])}
                                >
                                    <Download size={20} /> Download All
                                </button>
                                <button
                                    className="action-btn clear-all-btn"
                                    onClick={() => clearAllFiles(format)}
                                >
                                    <Trash2 size={20} />
                                </button>
                            </>
                        )}
                    </div>
                )}
            </div>
        )}
            
<input
    type="file"
    multiple
    accept="video/*,.mp4,.webm,.mov,.avi,.mkv,.wmv,.mpeg,.mpg,.3gp,.flv,.ogv"
    onChange={(e) => handleFileUpload(e, format)}
    style={{ display: 'none' }}
    ref={el => fileInputRefs.current[format] = el}
/>
        </div>
    );

    return (



        <div className="container">
            <Navbar />
            {popupMessages.length > 0 && (
                <PopupMessage
                    messages={popupMessages}
                    onClose={() => setPopupMessages([])}
                />
            )}
            {audioFormats.map((format, index) => (
                <div className="squares" key={index} data-format={format}>
                    {showTwoSquares ? (
                        <>
                            {renderSquare(format)}
                            {renderOutputSquare(format)}
                        </>
                    ) : (
                        loadingFormats.has(format) || files[format]?.length > 0
                            ? renderOutputSquare(format)
                            : renderSquare(format)
                    )}
                </div>
            ))}

            <div className="features-section">
    <div className="hero-section">
  <h1>Free Video to Audio Converter Online - Extract Audio from Any Video</h1>
  <p className="hero-description">Convert video files to high-quality audio formats instantly. Extract audio from MP4, AVI, MOV, MKV and more to MP3, WAV, FLAC, AAC, and OGG. No software installation required.</p>
</div>
  <h2>Why Choose Our Video to Audio Converter</h2>
  <div className="features-grid">
    <div className="feature">
      <h3>Professional Audio Quality</h3>
      <p>Extract crystal-clear audio from your videos with our advanced conversion engine. MP3 files are generated at 320kbps, FLAC uses maximum compression, ensuring studio-quality results.</p>
    </div>
    <div className="feature">
      <h3>100% Private & Secure</h3>
      <p>Your video files never leave your device. All processing happens locally in your browser, ensuring complete privacy and security for your personal content.</p>
    </div>
    <div className="feature">
      <h3>Multiple Video Formats</h3>
      <p>Support for all popular video formats including MP4, AVI, MOV, MKV, WMV, WEBM, 3GP, FLV, and MPEG. Convert any video to audio effortlessly.</p>
    </div>
    <div className="feature">
      <h3>Batch Video Processing</h3>
      <p>Save time by converting multiple video files simultaneously. Upload several videos and extract audio from all of them with one click.</p>
    </div>
  </div>
</div>

<div className="how-it-works">
  <h2>How to Convert Video to Audio</h2>
  <div className="steps">
    <div className="step">
      <div className="step-number">1</div>
      <p>Choose your desired audio format (MP3, WAV, OGG, AAC, or FLAC) from the available options</p>
    </div>
    <div className="step">
      <div className="step-number">2</div>
      <p>Upload your video files by clicking the format square or drag and drop your videos directly</p>
    </div>
    <div className="step">
      <div className="step-number">3</div>
      <p>Our converter automatically extracts the audio track from your video files</p>
    </div>
    <div className="step">
      <div className="step-number">4</div>
      <p>Download your converted audio files individually or all at once with the download all button</p>
    </div>
  </div>
</div>

<div className="faq-section">
  <h2>Frequently Asked Questions</h2>
  <div className="faq-item">
    <h3>Is this video to audio converter completely free?</h3>
    <p>Yes, our video to audio converter is 100% free with no hidden costs, watermarks, or usage limits. Convert as many videos as you need without any restrictions.</p>
  </div>
  <div className="faq-item">
    <h3>Which video formats can I convert to audio?</h3>
    <p>We support all major video formats including MP4, AVI, MOV, MKV, WMV, WEBM, 3GP, FLV, MPEG, and MPG. You can extract audio from virtually any video file.</p>
  </div>
  <div className="faq-item">
    <h3>What audio formats can I convert videos to?</h3>
    <p>You can convert your videos to MP3, WAV, OGG, AAC, and FLAC formats. Each format is optimized for different use cases and quality requirements.</p>
  </div>
  <div className="faq-item">
    <h3>Will the audio quality be preserved from the original video?</h3>
    <p>Yes, we extract audio at the highest quality possible. MP3 files are encoded at 320kbps, FLAC uses lossless compression, and other formats maintain professional-grade quality standards.</p>
  </div>
  <div className="faq-item">
    <h3>What's the maximum video file size I can convert?</h3>
    <p>You can convert video files up to 500MB in size. This accommodates most video files including full-length movies, lectures, and large media content.</p>
  </div>
  <div className="faq-item">
    <h3>Are my video files safe and private?</h3>
    <p>Absolutely. All video processing happens locally in your browser. Your files are never uploaded to any server, ensuring complete privacy and security of your content.</p>
  </div>
  <div className="faq-item">
    <h3>Can I convert multiple videos to audio at once?</h3>
    <p>Yes, our tool supports batch conversion. Upload multiple video files simultaneously and convert them all to your chosen audio format with ease.</p>
  </div>
</div>

<div className="use-cases">
  <h2>Popular Video to Audio Conversion Use Cases</h2>
  <div className="use-cases-grid">
    <div className="use-case">
      <h3>Music Video to Audio</h3>
      <p>Extract audio tracks from music videos, concerts, and live performances to create your personal music library in high-quality formats.</p>
    </div>
    <div className="use-case">
      <h3>Educational Content</h3>
      <p>Convert lecture videos, tutorials, and webinars to audio format for convenient listening during commutes or workouts.</p>
    </div>
    <div className="use-case">
      <h3>Podcast Creation</h3>
      <p>Transform video interviews, discussions, and recordings into podcast-ready audio files for easy distribution and streaming.</p>
    </div>
    <div className="use-case">
      <h3>Mobile Storage Optimization</h3>
      <p>Convert videos to audio to save significant storage space on your mobile devices while keeping the content you love.</p>
    </div>
    <div className="use-case">
      <h3>Audio Editing Projects</h3>
      <p>Extract audio tracks from videos as source material for audio editing, remixing, and professional production work.</p>
    </div>
    <div className="use-case">
      <h3>Language Learning</h3>
      <p>Convert foreign language videos to audio files for focused listening practice and language immersion training.</p>
    </div>
  </div>
</div>

<div className="about-section">
  <h2>About Our Video to Audio Converter</h2>
  <p>Our free online video to audio converter leverages cutting-edge FFmpeg technology running entirely in your browser to extract high-quality audio from video files. Built with privacy and simplicity in mind, our tool requires no software installation or account registration.</p>
  <p>Whether you're extracting music from videos, creating audio content for podcasts, or optimizing storage space on your devices, our converter delivers professional-grade results. We support all major video and audio formats, ensuring compatibility with your workflow and requirements.</p>
  <p>The converter processes everything locally on your device, meaning your video files never leave your computer. This approach ensures maximum privacy while delivering fast, reliable conversions without depending on internet speed for file uploads.</p>
</div>
            
            <style jsx>{`
                .container {
                    display: flex;
                    flex-direction: column;
                    justify-content: flex-start;
                    align-items: center;
                    margin-top:33.33vh;
                     
                    box-sizing: border-box;
                    width: 100%;
                    min-height: 100vh;
                    
                }
                .squares {
                    display: flex;
                    gap: 6.09vw;
                    width: 100%;
                    justify-content: center;
                    margin-bottom: 33.33vh;
                }
                .square {
                    width: 44.44vh;
                    height: 33.33vh;
                    background-color: #5856D6;
                    position: relative;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    cursor: pointer;
                    border-radius: 1.11vh;
                    box-sizing: border-box;
                }

                .square.output {
    width: 33.33vh;  // Specific width for output square
    overflow: hidden;
    background: #D9D9D9;
    border: none;
    padding: 0;
}
                .format {
                    color: white;
                    font-size: 5.28vh;
                    font-weight: bold;
                    pointer-events: none;
                     font-family: "DM Sans", sans-serif;
  font-optical-sizing: auto;
  font-weight: 400;
  font-style: normal;
                }

                .format-input {
    font-size: 4.17vh;  /* Adjust this value to your preferred size */
}
               
                .output.has-files {
                    background: transparent;
                }
                .file-list-container {
                    display: flex;
                    flex-direction: column;
                    height: 100%;
                    width: 100%;
                    font-family: "Lexend", sans-serif;
    font-optical-sizing: auto;
    font-weight: 400;
    font-style: normal;
                }
                .file-list {
                    flex: 1;
                    overflow-y: auto;
                    display: flex;
                    flex-direction: column;
                    gap: 1.11vh;
                    padding-right: var(--padding-right, 0);
                    overscroll-behavior: contain; 
                }
                .file-list::-webkit-scrollbar {
                    width: 1.67vh;
                }
                .file-list::-webkit-scrollbar-track {
                    background-color: #D9D9D9;
                    border-radius: 1.11vh;
                }
                .file-list::-webkit-scrollbar-thumb {
                   background-color: rgba(27, 31, 38, 0.72);
                    border-radius: 0.74vh;
                    border: 0.37vh solid transparent;
                    background-clip: padding-box;
                }
                .file-list::-webkit-scrollbar-thumb:vertical {
                    min-height: 24px;
                }
                .file-list::-webkit-scrollbar-button {
                    height: 0vh;
                    background-color: #000;
                }
                .file-item {
                    display: flex;
                    align-items: center;
                    height: 4.63vh;
                    gap: 1.11vh;
                    padding: 0 0;
                }
                .filename-container {
                    flex: 1;
                    overflow: hidden;
                    height: 100%;
                    display: flex;
                    align-items: center;
                    background: #EBEDF0;
                    border-radius: 1.11vh;
                    position: relative;
                }
                .fade-outer, .fade-inner {
                    position: absolute;
                    top: 0;
                    bottom: 0;
                    z-index: 2;
                }
                .fade-outer.fade-left {
                    width: 1.11vh;
                    left: 1.11vh;
                    background: linear-gradient(to right, #EBEDF0, rgba(235,237,240,0));
                }
               .fade-outer.fade-right {
                    width: 1.11vh;
                    right: 1.11vh;
                    background: linear-gradient(to left, #EBEDF0, rgba(235,237,240,0));
                }
                .fade-inner.fade-left {
                    width: 1.11vh;
                    left: 0px;
                    background: #EBEDF0;
                }
                .fade-inner.fade-right {
                    width: 1.11vh;
                    right: 0px;
                    background: #EBEDF0;
                }
                .filename-scroll {
                    display: flex;
                    white-space: nowrap;
                    animation: scroll-left 20s linear infinite;
                   
                }
                .filename-content {
                    display: flex;
                    padding: 0 1.11vh;
                }
             .file-name {
                    color: #000;
                    font-size: 1.67vh;
                    font-weight: 500;
                    padding-right: 2.22vh;
                }
               .download-btn {
                    background-color: #007AFF;
                    color: white;
                    border: none;
                    width: 4.63vh;
                    height: 4.63vh;
                    border-radius: 1.11vh;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: background-color 0.3s ease;
                    padding: 0;
                }
                .download-btn svg {
                    width: 2.22vh; 
                    height: 2.22vh;               
                }
                .download-btn:hover {
                    background-color: #0056b3;
                }
                 .button-container {
                    display: flex;
                    justify-content: space-between;
                    padding-top: 1.11vh;
                }
                .action-btn {
                    color: white;
                    border: none;
                    height: 4.63vh;
                    border-radius: 4px;
                    font-size: 14px;
                    font-weight: 500;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 1.11vh;
                    cursor: pointer;
                    transition: background-color 0.3s ease;
                    padding: 0 1.11vh;
                }
                .upload-btn {
                    background-color: #FF9500;
                    flex-grow: 1;
                    margin-right: 1.11vh;
                    width:13.33vh;
                    border-radius:1.11vh;
                    font-family: "DM Sans", sans-serif;
font-optical-sizing: auto;
font-weight: 400;
font-style: normal;
                }
                .upload-btn:hover {
                    background-color: #138496;
                }
                   .download-all-btn {
                    background-color: #34C759;
                    flex-grow: 2;
                    font-size: 1.48vh;
                    border-radius: 1.11vh;
                    font-family: "Lexend", sans-serif;
    font-optical-sizing: auto;
    font-weight: 400;
    font-style: normal;
                }
                .download-all-btn:hover {
                    background-color: #218838;
                }
                .clear-all-btn {
                    background-color: #FF3B30;
                    width: 4.63vh;
                    height: 4.63vh;
                    margin-left: 1.11vh;
                    border-radius: 1.11vh;
                }
                .clear-all-btn:hover {
                    background-color: #c82333;
                }
             .action-btn svg {
  width: 2.22vh;
  height: 2.22vh;
}
                .loading {
                    height: 4.63vh;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #333;
                    font-size: 14px;
                    background: #ffffff;
                    padding: 0 1.11vh;
                }
                @keyframes scroll-left {
                    0% {
                        transform: translateX(0);
                    }
                    100% {
                        transform: translateX(-50%);
                    }
                }
.hero-section {
  text-align: center;
  max-width: 800px;
  margin: 0vh auto 18vh auto;
  padding: 0px;
}

.hero-section h1 {
  font-size: 3.5vh;
  color: #333;
  margin-bottom: 2vh;
  font-family: "DM Sans", sans-serif;
}

.hero-description {
  font-size: 2vh;
  color: #666;
  line-height: 1.5;
  font-family: "Lexend", sans-serif;
}

.features-section, .how-it-works, .faq-section, .use-cases, .about-section {
  max-width: 1200px;
  margin: 10vh auto;
  padding: 0 20px;
}

.features-section{
  max-width: 1200px;
  margin: -5vh auto 10vh auto;
  padding: 0 20px;
}


h2 {
  font-size: 2.8vh;
  color: #333;
  text-align: center;
  margin-bottom: 5vh;
  font-family: "DM Sans", sans-serif;
}

.features-grid, .use-cases-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
}

.feature, .use-case {
  background: #f8f9fa;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
}

.feature h3, .use-case h3, .faq-item h3 {
  font-size: 2vh;
  color: #5856D6;
  margin-bottom: 10px;
  font-family: "DM Sans", sans-serif;
}

.feature p, .use-case p, .faq-item p, .about-section p {
  font-size: 1.6vh;
  color: #555;
  line-height: 1.6;
  font-family: "Lexend", sans-serif;
}

.steps {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 20px;
}

.step {
  flex: 1 1 200px;
  display: flex;
  align-items: center;
  background: #f8f9fa;
  border-radius: 10px;
  padding: 15px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
}

.step-number {
  background: #5856D6;
  color: white;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin-right: 15px;
  flex-shrink: 0;
}

.step p {
  margin: 0;
  font-size: 1.6vh;
  color: #555;
  font-family: "Lexend", sans-serif;
}

.faq-item {
  background: #f8f9fa;
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
}

@media (max-width: 768px) {
  .features-grid, .use-cases-grid {
    grid-template-columns: 1fr;
  }
  
  .steps {
    flex-direction: column;
  }
}

                
            `}</style>

        </div>
    );
};


export default VideotoAudio;

